import { useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';

interface UseAuthTimeoutOptions {
  warningTime?: number; // Time in ms before logout to show warning (default: 5 minutes)
  checkInterval?: number; // How often to check (default: 1 minute)
  inactiveTimeout?: number; // Total inactivity time before logout (default: 2 hours)
}

/**
 * Hook to handle authentication timeouts and show warnings
 */
export function useAuthTimeout(options: UseAuthTimeoutOptions = {}) {
  const { isAuthenticated, logout } = useAuth();
  const { 
    warningTime = 5 * 60 * 1000, 
    checkInterval = 60 * 1000,
    inactiveTimeout = 120 * 60 * 1000 // 2 hours
  } = options;
  const warningShownRef = useRef(false);
  const logoutTimerRef = useRef<number | null>(null);
  const warningTimerRef = useRef<number | null>(null);

  useEffect(() => {
    if (!isAuthenticated) {
      // Clear any existing timers when user logs out
      if (logoutTimerRef.current) {
        clearTimeout(logoutTimerRef.current);
        logoutTimerRef.current = null;
      }
      if (warningTimerRef.current) {
        clearTimeout(warningTimerRef.current);
        warningTimerRef.current = null;
      }
      warningShownRef.current = false;
      return;
    }

    // Reset warning state when user is authenticated
    warningShownRef.current = false;

    // Initialize activity tracking on auth
    const initializeActivity = () => {
      const currentTime = Date.now().toString();
      localStorage.setItem('last_user_activity', currentTime);
    };

    // Only initialize if no existing activity timestamp or if it's very old
    const lastActivity = localStorage.getItem('last_user_activity');
    if (!lastActivity) {
      initializeActivity();
    }

    // Set up periodic check for inactivity
    const interval = setInterval(() => {
      // Check for user inactivity
      const lastActivity = localStorage.getItem('last_user_activity');
      if (lastActivity) {
        const lastActivityTime = parseInt(lastActivity, 10);
        const currentTime = Date.now();
        const timeSinceActivity = currentTime - lastActivityTime;
        
        // Show warning if user has been inactive for more than (inactiveTimeout - warningTime)
        if (timeSinceActivity > (inactiveTimeout - warningTime) && !warningShownRef.current) {
          warningShownRef.current = true;
          toast.error(`You will be logged out in ${Math.ceil(warningTime / 60000)} minutes due to inactivity.`, {
            duration: warningTime - 1000, // Show warning until logout
          });
        }
        
        // If user has been inactive for more than the timeout period, logout
        if (timeSinceActivity > inactiveTimeout) {
          logout();
        }
      } else {
        // If no activity timestamp, initialize it instead of logging out
        initializeActivity();
      }
    }, checkInterval);

    // Track user activity
    const trackActivity = () => {
      localStorage.setItem('last_user_activity', Date.now().toString());
      // Reset warning state when user activity is detected
      if (warningShownRef.current) {
        warningShownRef.current = false;
      }
    };

    // Add event listeners for user activity
    window.addEventListener('mousemove', trackActivity);
    window.addEventListener('keypress', trackActivity);
    window.addEventListener('click', trackActivity);
    window.addEventListener('scroll', trackActivity);
    // Add additional events for better activity tracking
    window.addEventListener('touchstart', trackActivity);
    window.addEventListener('touchmove', trackActivity);
    window.addEventListener('focus', trackActivity);

    return () => {
      clearInterval(interval);
      window.removeEventListener('mousemove', trackActivity);
      window.removeEventListener('keypress', trackActivity);
      window.removeEventListener('click', trackActivity);
      window.removeEventListener('scroll', trackActivity);
      window.removeEventListener('touchstart', trackActivity);
      window.removeEventListener('touchmove', trackActivity);
      window.removeEventListener('focus', trackActivity);
      
      if (logoutTimerRef.current) {
        clearTimeout(logoutTimerRef.current);
      }
      if (warningTimerRef.current) {
        clearTimeout(warningTimerRef.current);
      }
    };
  }, [isAuthenticated, logout, warningTime, checkInterval, inactiveTimeout]);
}